import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import InfoBox from '../../Shared/InfoBox'
import Notice from '../../Shared/Notice'
import TicketSplitBar from './TicketSplitBar'
import {
  fetchTicketCategoriesCapacitySplit,
  fetchTicketTypesCapacitySplit,
} from '../../../api/charts'

const TABS = [
  {
    id: 'price',
    label: 'Event.ticketBreakdown.tabs.price',
    tips: 'Tips.Events.TicketCategories',
    fetchData: fetchTicketCategoriesCapacitySplit,
  },
  {
    id: 'type',
    label: 'Event.ticketBreakdown.tabs.type',
    tips: 'Tips.Events.TicketType',
    fetchData: fetchTicketTypesCapacitySplit,
  },
]

/**
 * Groups the two "share of the capacity" views (by price range, by ticket type)
 * behind one card with a tab switcher, since they both split the same capacity
 * from a different angle.
 * @Tags( event, presentational )
 */
export default function TicketBreakdown({ eventId, capacity }) {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState(TABS[0].id)
  const [tabsWithData, setTabsWithData] = useState(() =>
    Object.fromEntries(TABS.map((tab) => [tab.id, true])),
  )

  const hasDataHandlers = useMemo(
    () =>
      Object.fromEntries(
        TABS.map((tab) => [
          tab.id,
          (hasData) =>
            setTabsWithData((previous) =>
              previous[tab.id] === hasData ? previous : { ...previous, [tab.id]: hasData },
            ),
        ]),
      ),
    [],
  )

  // Both breakdowns are shares of the capacity, so without one there is nothing
  // to show — the same warning the sales trend gives takes the place of the bars.
  if (!(capacity > 0)) {
    return (
      <div className="tabs-component_main-content_section">
        <div className="ticket-breakdown_header">
          <InfoBox content={TABS[0].tips}>
            <h2 className="card-title-default d-inline-block m-0">
              {t('Event.cards.ticketBreakdown')}
            </h2>
          </InfoBox>
        </div>
        <Notice isVisible message={t('Event.salesTrend.capacityNotSet')} />
      </div>
    )
  }

  const availableTabs = TABS.filter((tab) => tabsWithData[tab.id])
  const hasAnyTab = availableTabs.length > 0
  const currentTab = hasAnyTab
    ? availableTabs.some((tab) => tab.id === activeTab)
      ? activeTab
      : availableTabs[0].id
    : activeTab
  const activeTabMeta = TABS.find((tab) => tab.id === currentTab)

  return (
    <div
      className="tabs-component_main-content_section"
      // The chart components below must stay mounted even while every tab is
      // momentarily empty (e.g. capacity hasn't loaded yet on first render) —
      // unmounting them here would mean they can never report data again.
      style={{ display: hasAnyTab ? 'block' : 'none' }}
    >
      <div className="d-flex justify-content-between align-items-center flex-wrap ticket-breakdown_header">
        <InfoBox content={activeTabMeta?.tips}>
          <h2 className="card-title-default d-inline-block m-0">{t('Event.cards.ticketBreakdown')}</h2>
        </InfoBox>

        {availableTabs.length > 1 && (
          <div className="ticket-breakdown_tabs">
            {availableTabs.map((tab) => (
              <div
                key={tab.id}
                className={currentTab === tab.id ? 'selected' : ''}
                onClick={() => setActiveTab(tab.id)}
              >
                <span>{t(tab.label)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="ticket-breakdown_showing">{t('Event.ticketBreakdown.showing')}</p>

      {TABS.map((tab) => (
        <div key={tab.id} style={{ display: currentTab === tab.id ? 'block' : 'none' }}>
          <TicketSplitBar
            eventId={eventId}
            capacity={capacity}
            fetchData={tab.fetchData}
            onHasData={hasDataHandlers[tab.id]}
          />
        </div>
      ))}
    </div>
  )
}

TicketBreakdown.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  capacity: PropTypes.number,
}
